/**
 * MentionTextarea - a textarea that can @mention colleagues (WPS-153 item 1).
 *
 * Typing `@` opens a colleague picker filtered by what follows it; choosing
 * someone replaces the token with their name and records them as a mention.
 * The names are held alongside the text rather than parsed back out of it,
 * because two colleagues can share a display name and the person notified must
 * never be a guess.
 *
 * Generic on purpose: it takes a list of candidates and knows nothing about
 * claims, registrations, or which surface it is on.
 */

import { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { KeyboardEvent } from 'react'

import { Button } from '@/shared/components/ui/button'
import { Textarea } from '@/shared/components/ui/textarea'
import { cn } from '@/shared/lib/utils'

export interface MentionUser {
  uuid: string
  name: string
  email: string
  /** Already working this record (assignee, contact, prior note author). */
  on_record?: boolean
}

export interface MentionTextareaProps {
  value: string
  onChange: (value: string) => void
  /** Everyone who could be mentioned - normally the caller's colleagues. */
  candidates: MentionUser[]
  /** Who is currently mentioned in ``value``. */
  mentions: MentionUser[]
  onMentionsChange: (mentions: MentionUser[]) => void
  placeholder?: string
  maxLength?: number
  disabled?: boolean
  id?: string
  className?: string
}

/** The `@query` being typed at the caret, or null when the caret is not in one. */
function activeMentionQuery(value: string, caret: number): { start: number; query: string } | null {
  const upToCaret = value.slice(0, caret)
  const at = upToCaret.lastIndexOf('@')
  if (at === -1) return null
  // Only an `@` that starts a word opens the picker, so an email address
  // typed into a note does not.
  const before = at === 0 ? '' : upToCaret[at - 1]
  if (before && !/\s/.test(before)) return null
  const query = upToCaret.slice(at + 1)
  // A mention is a name, so it never spans a line and stays short. Anything
  // longer is prose that happened to follow an `@`.
  if (/[\n]/.test(query) || query.length > 40) return null
  return { start: at, query }
}

export function MentionTextarea({
  value,
  onChange,
  candidates,
  mentions,
  onMentionsChange,
  placeholder,
  maxLength,
  disabled,
  id,
  className,
}: MentionTextareaProps) {
  const { t } = useTranslation()
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const [trigger, setTrigger] = useState<{ start: number; query: string } | null>(null)
  const [highlighted, setHighlighted] = useState(0)

  const matches = useMemo(() => {
    if (!trigger) return []
    const query = trigger.query.trim().toLowerCase()
    const alreadyMentioned = new Set(mentions.map((m) => m.uuid))
    return candidates
      .filter((user) => !alreadyMentioned.has(user.uuid))
      .filter(
        (user) =>
          !query ||
          user.name.toLowerCase().includes(query) ||
          user.email.toLowerCase().includes(query),
      )
      .slice(0, 6)
  }, [candidates, mentions, trigger])

  // Drop anyone whose name no longer appears in the text, so deleting a
  // mention by hand also stops the notification.
  const syncMentions = useCallback(
    (nextValue: string) => {
      const surviving = mentions.filter((user) => nextValue.includes(`@${user.name}`))
      if (surviving.length !== mentions.length) onMentionsChange(surviving)
    },
    [mentions, onMentionsChange],
  )

  const handleChange = useCallback(
    (nextValue: string, caret: number) => {
      onChange(nextValue)
      syncMentions(nextValue)
      const next = activeMentionQuery(nextValue, caret)
      setTrigger(next)
      setHighlighted(0)
    },
    [onChange, syncMentions],
  )

  const insertMention = useCallback(
    (user: MentionUser) => {
      if (!trigger) return
      const caret = textareaRef.current?.selectionStart ?? value.length
      const nextValue = `${value.slice(0, trigger.start)}@${user.name} ${value.slice(caret)}`
      onChange(nextValue)
      if (!mentions.some((m) => m.uuid === user.uuid)) {
        onMentionsChange([...mentions, user])
      }
      setTrigger(null)
      setHighlighted(0)
      // Put the caret back after the name we just inserted.
      const nextCaret = trigger.start + user.name.length + 2
      window.requestAnimationFrame(() => {
        const node = textareaRef.current
        if (!node) return
        node.focus()
        node.setSelectionRange(nextCaret, nextCaret)
      })
    },
    [mentions, onChange, onMentionsChange, trigger, value],
  )

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if (!trigger || matches.length === 0) return
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        setHighlighted((current) => (current + 1) % matches.length)
      } else if (event.key === 'ArrowUp') {
        event.preventDefault()
        setHighlighted((current) => (current - 1 + matches.length) % matches.length)
      } else if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault()
        insertMention(matches[highlighted])
      } else if (event.key === 'Escape') {
        event.preventDefault()
        setTrigger(null)
      }
    },
    [highlighted, insertMention, matches, trigger],
  )

  return (
    <div className={cn('relative', className)}>
      <Textarea
        id={id}
        ref={textareaRef}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        maxLength={maxLength}
        aria-describedby={id ? `${id}-mention-hint` : undefined}
        onChange={(event) =>
          handleChange(event.target.value, event.target.selectionStart ?? event.target.value.length)
        }
        onKeyDown={handleKeyDown}
        onBlur={() => window.setTimeout(() => setTrigger(null), 120)}
      />
      {trigger && matches.length > 0 && (
        <div
          role="listbox"
          aria-label={t('notes.mention.listLabel', 'Mention a colleague')}
          className="absolute left-0 right-0 top-full z-50 mt-1 max-h-[196px] overflow-y-auto rounded-[6px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] py-1 shadow-lg"
        >
          {matches.map((user, index) => (
            <div key={user.uuid}>
              {/* One divider where the record's own people end, so the ranking
                  is legible instead of looking like an odd sort order. */}
              {index > 0 &&
                matches[index - 1].on_record &&
                !user.on_record && (
                  <p className="px-3 pb-1 pt-2 text-[9px] font-normal uppercase tracking-[0.12em] text-[var(--ink-3)]">
                    Everyone else
                  </p>
                )}
              <Button
              type="button"
              variant="ghost"
              role="option"
              aria-selected={index === highlighted}
              // Fires before blur closes the list.
              onMouseDown={(event) => {
                event.preventDefault()
                insertMention(user)
              }}
              onMouseEnter={() => setHighlighted(index)}
              className={cn(
                'flex h-auto w-full cursor-pointer flex-col items-start gap-0 rounded-none px-3 py-1.5 text-left',
                index === highlighted ? 'bg-[var(--bg-subtle)]' : 'bg-transparent',
              )}
            >
              <span className="text-[13px] text-[var(--ink)]">{user.name}</span>
              <span className="text-[11px] text-[var(--ink-3)]">{user.email}</span>
              </Button>
            </div>
          ))}
        </div>
      )}
      {mentions.length > 0 && (
        <p
          id={id ? `${id}-mention-hint` : undefined}
          className="mt-1.5 text-[11px] text-[var(--ink-3)]"
        >
          {/* Label and names kept separate rather than interpolated: the
              names are data, and this way the hint still reads correctly
              before the translation catalogue has loaded. */}
          {t('notes.mention.notifying', 'Notifying')}{' '}
          {mentions.map((user) => user.name).join(', ')}
        </p>
      )}
    </div>
  )
}

/**
 * Render a note body with its mentions picked out, so a reader can see at a
 * glance who was pulled in. Matching is driven by the note's recorded mentions,
 * never by a regex over the prose, so an `@` in ordinary text stays ordinary.
 */
export function MentionedText({
  text,
  mentions,
  className,
}: {
  text: string
  mentions: { name: string }[]
  className?: string
}) {
  const names = mentions.map((m) => m.name).filter(Boolean)
  if (names.length === 0) return <span className={className}>{text}</span>

  // Longest first, so "@Ada Bell-Smith" is not eaten by "@Ada Bell".
  const pattern = new RegExp(
    `(${names
      .slice()
      .sort((a, b) => b.length - a.length)
      .map((name) => `@${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`)
      .join('|')})`,
    'g',
  )

  return (
    <span className={className}>
      {text.split(pattern).map((part, index) =>
        names.some((name) => part === `@${name}`) ? (
          <span
            key={`${part}-${index}`}
            className="rounded-[3px] bg-[var(--green-bg)] px-1 text-[var(--green)]"
          >
            {part}
          </span>
        ) : (
          part
        ),
      )}
    </span>
  )
}
